import React, { useState } from 'react';

/**
 * Lets a PM add more teams to an existing "Selected teams" RAMS/Permits document
 * after it's already been uploaded (e.g. a new subcontractor team joins later).
 * Add-only: teams already assigned show as checked and locked, existing signatures
 * are never affected.
 */
const AddTeamsModal = ({ document, teams, onClose, onSave }) => {
  const existingIds = document.selectedTeams || [];
  const [selectedIds, setSelectedIds] = useState(existingIds);
  const [saving, setSaving] = useState(false);

  const hasNewSelection = selectedIds.some(id => !existingIds.includes(id));

  const handleSave = async () => {
    if (!hasNewSelection || saving) return;
    setSaving(true);
    try {
      await onSave(selectedIds);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--cc-card-bg)',
          borderRadius: '8px',
          padding: '1.5rem',
          maxWidth: '420px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto'
        }}
      >
        <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: 600 }}>Add teams</h3>
        <p
          style={{
            margin: '0 0 1rem 0',
            fontSize: '0.8125rem',
            color: 'var(--cc-text-muted)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
          title={document.name}
        >
          {document.name}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {teams.map(team => {
            const isExisting = existingIds.includes(team.id);
            const isChecked = selectedIds.includes(team.id);
            return (
              <label
                key={team.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  background: isChecked ? '#f0f9ff' : 'var(--branding-app-surface-panel)',
                  border: `1px solid ${isChecked ? '#0ea5e9' : '#e2e8f0'}`,
                  borderRadius: '6px',
                  cursor: isExisting ? 'default' : 'pointer',
                  fontSize: '0.875rem',
                  opacity: isExisting ? 0.7 : 1
                }}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={isExisting}
                  onChange={() => {
                    setSelectedIds(prev =>
                      prev.includes(team.id)
                        ? prev.filter(id => id !== team.id)
                        : [...prev, team.id]
                    );
                  }}
                />
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: team.color || 'var(--branding-app-primary)' }} />
                <span>{team.name}</span>
                {isExisting && (
                  <span style={{ fontSize: '0.7rem', color: 'var(--cc-text-muted)' }}>(already assigned)</span>
                )}
              </label>
            );
          })}
          {teams.length === 0 && (
            <p style={{ fontSize: '0.875rem', color: 'var(--cc-text-muted)' }}>No teams on this project yet.</p>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
          <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="btn-primary"
            disabled={!hasNewSelection || saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTeamsModal;
